import { useState, useEffect, useCallback } from 'react';
import AdvertiserDetails from '../../Hooks/Dashboard/useAdvertiser';
import {
  generateAdvertiserToken,
  getStoredToken,
  storeToken,
  isTokenExpired,
  getAdvertiserIdsFromToken
} from '../../utils/jwtTokenHelper';

/**
 * Custom hook for managing advertiser token
 *
 * Features:
 * - Fetches advertisers only if token is missing or expired
 * - Generates JWT token with all advertiser IDs
 * - Stores token in localStorage for 24 hours
 * - Auto-refreshes expired tokens
 *
 * @returns {Object} Token state and methods
 */
export const useAdvertiserToken = () => {
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { advertiser, getAdvertiser, loading: fetchingAdvertisers } = AdvertiserDetails();

  /**
   * Create token from advertiser list
   */
  const createToken = useCallback((advertiserList) => {
    try {
      if (!advertiserList || advertiserList.length === 0) {
        throw new Error('No advertisers available to create token');
      }

      // Extract advertiser IDs
      const advertiserIds = advertiserList.map(adv => adv.id);

      // Generate JWT token
      const newToken = generateAdvertiserToken(advertiserIds);

      // Store token
      storeToken(newToken);
      setToken(newToken);
      setError(null);

      console.log(`Token created with ${advertiserIds.length} advertisers, valid for 24 hours`);

      return newToken;
    } catch (err) {
      console.error('Error creating token:', err);
      setError(err.message);
      return null;
    }
  }, []);

  /**
   * Initialize token on mount
   */
  useEffect(() => {
    const initializeToken = async () => {
      setLoading(true);

      // Check if valid token exists
      const existingToken = getStoredToken();

      if (existingToken && !isTokenExpired(existingToken)) {
        // Valid token exists, use it
        setToken(existingToken);
        setLoading(false);

        const advertiserIds = getAdvertiserIdsFromToken();
        console.log(`Using existing token with ${advertiserIds?.length || 0} advertisers`);
      } else {
        // No valid token, fetch advertisers
        console.log('No valid token found, fetching advertisers...');
        try {
          await getAdvertiser();
        } catch (err) {
          console.error('Error fetching advertisers:', err);
          setError('Failed to fetch advertisers');
          setLoading(false);
        }
      }
    };

    initializeToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  /**
   * Create token when advertisers are fetched
   */
  useEffect(() => {
    if (advertiser && advertiser.length > 0 && !token) {
      createToken(advertiser);
      setLoading(false);
    } else if (advertiser && advertiser.length === 0 && !fetchingAdvertisers && !token) {
      // Handle empty advertiser list - stop trying to fetch
      console.warn('No advertisers available from API');
      setError('No advertisers available');
      setLoading(false);
    }
  }, [advertiser, token, createToken, fetchingAdvertisers]);

  /**
   * Stop loading if fetching fails
   */
  useEffect(() => {
    if (!fetchingAdvertisers && !token && !advertiser?.length) {
      setLoading(false);
    }
  }, [fetchingAdvertisers, token, advertiser]);

  /**
   * Manually refresh token
   */
  const refreshToken = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      await getAdvertiser();
    } catch (err) {
      console.error('Error refreshing token:', err);
      setError('Failed to refresh token');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // getAdvertiser is stable from custom hook

  /**
   * Get token (auto-refresh if expired)
   */
  const getToken = useCallback(() => {
    const currentToken = getStoredToken();

    if (!currentToken || isTokenExpired(currentToken)) {
      // Token expired, trigger refresh
      refreshToken();
      return null;
    }

    return currentToken;
  }, [refreshToken]);

  return {
    token,
    loading,
    error,
    refreshToken,
    getToken,
    advertiserIds: token ? getAdvertiserIdsFromToken() : null
  };
};
